import { PreferenceState } from '../reducers/preference';
import { FontFamily, ColorScheme } from '../actions/PreferenceAction';
import * as xmlns from '../../../../common/src/ts/xmlns';
import { Store } from 'redux';
import { AppStates } from '../reducers/app';
import { ViewerToContent, ContentToViewer } from '../types/messages';
import { ContentStorageState } from '../reducers/contentStorage';
import { MarkerInfo } from '../marker';
import { forEachIterator, iterateEntries } from '../../../../common/src/ts/utils';
import { FrameInfo } from '../../../../common/src/ts/types/FrameInfo';
import {
  getContentWindow, getContentDocument, isWindow,
} from '../../../../common/src/ts/utils/dom';
import { getBasePath } from '../utils/browser';
import { PagesState, isPopupPanelID } from '../reducers/pages';
import { findTabByID, getActiveFrameId } from '../helpers/pages';
import { Rectangle, Dimension } from 'ark-common';
import { sneCustomColors } from '../drawing/constants';
import { UIModeState, initUIModeState } from '../reducers/uimode';
import { UIMode } from '../../../../common/src/ts/types/UIMode';

// Similar to PreferenceState, but no need to be persisted.
interface EphemeralStyle {
  visibleRect: Rectangle;
  visualScale: number; // Only for "scaleBehavior: transform" sheets, otherwise it's always "1".
  rootContainerSize: Dimension;
  popupCollapsed: boolean;
}

interface Frame {
  ref: HTMLIFrameElement;
  panelID: string;
  started: boolean;
  shouldApplyReflowableContentSettings: boolean;
}

class FrameStore {
  private frames: { [key: string]: Frame };
  private panelIDToFrameIDs: { [panelID: string]: Set<string> } = {};
  private reverseRefs: WeakMap<Window, FrameInfo> = new WeakMap();
  private uimode: Readonly<UIModeState> = initUIModeState();
  private preference: Readonly<PreferenceState> | undefined;
  private contentStorage: Readonly<ContentStorageState> | undefined;
  private markers: ReadonlyArray<MarkerInfo> = [];
  private pages: PagesState | null = null;
  private clickHandlers: ((info: FrameInfo, ev: MouseEvent) => any)[] = [];
  private ephemeralStyles: { [key: string]: EphemeralStyle };
  private popupRects: { [key: string]: Rectangle };
  private messageHandlers: { [key: string]: ((info: FrameInfo, data: any) => any)[]; } = {};
  private readonly baseDir: string;
  private lastActiveFrameId: string | null;

  public constructor() {
    this.frames = {};
    this.ephemeralStyles = {};
    this.popupRects = {};
    this.baseDir = getBasePath();
    this.lastActiveFrameId = null;

    window.addEventListener('message', (ev: MessageEvent) => {
      const { source, data: message } = ev;
      if (
        !ContentToViewer.isDomesticMessage(message)
        || source === null
        || source === undefined
        || !isWindow(source)
      ) {
        return;
      }

      // XXX : なぜかif文だと動かない
      switch (message.type) {
        case ContentToViewer.MessageType.PageStartup: {
          const frameID = message.data.frameID;
          this.frames[frameID].started = true;

          this.notifyViewerUIModeChange(source, this.uimode);
          this.applyPreferenceToWindow(source, frameID);
          this.notifyContentStorageToWindow(source);
          this.postMessageToWindow(source,
                                   ViewerToContent.MessageType.NotifyUpdateMarker,
                                   this.markers);

          const style = this.ephemeralStyles[frameID];
          if (style !== undefined) {
            this.notifyVisualPropertiesToWindow(source, style);
          }
          const popupRect = this.popupRects[frameID];
          if (popupRect !== undefined) {
            this.notifyPopupRectToWindow(source, popupRect);
          }
          const { panelID } = this.frames[frameID];
          let isVisible = false;
          if (this.pages !== null && this.pages.tabs.length > 0) {
            const tabPanelID = (
              isPopupPanelID(panelID) ? this.pages.popups[panelID].parent : panelID
            );
            isVisible = this.pages.tabs[this.pages.activeTab].id === tabPanelID;
          }
          this.notifyVisibilityChangeToWindow(source, isVisible);

          if (this.pages !== null) {
            const activeFrameId = getActiveFrameId(this.pages);
            const isFocused = frameID === activeFrameId;
            // FIXME: frameIDをそのまま使っている
            this.notifyFocusChange(source, isFocused, activeFrameId);
            if (isFocused) {
              this.lastActiveFrameId = frameID;
            }
          }

          break;
        }
        default: {
          if (!(message.type in this.messageHandlers) || !this.reverseRefs.has(source)) {
            break;
          }
          const info = this.reverseRefs.get(source)!;
          for (const handler of this.messageHandlers[message.type]) {
            handler(info, message);
          }
          break;
        }
      }
    });
  }

  public async registerFrame(info: FrameInfo, ref: HTMLIFrameElement) {
    if (ref == null) {
      throw new Error('Ref is null.');
    }

    const existing = this.frames[info.frameID];
    if (existing !== undefined && existing.ref.src === ref.src) {
      // Maybe same source, do nothing.
      return;
    }

    this.frames[info.frameID] = {
      ref,
      started: false,
      shouldApplyReflowableContentSettings: info.shouldApplyReflowableContentSettings,
      panelID: info.panelID,
    };
    if (!(info.panelID in this.panelIDToFrameIDs)) {
      this.panelIDToFrameIDs[info.panelID] = new Set();
    }
    this.panelIDToFrameIDs[info.panelID].add(info.frameID);
    const doc = await getContentDocument(ref);
    this.injectScriptAndStyleToDoc(doc, info);
  }

  public addClickEventHandler(handler: (info: FrameInfo, ev: MouseEvent) => any) {
    const index = this.clickHandlers.indexOf(handler);
    if (index < 0) {
      this.clickHandlers.push(handler);
    }
  }

  public addMessageHandler(type: string, handler: (info: FrameInfo, data: any) => any) {
    if (!(type in this.messageHandlers)) {
      this.messageHandlers[type] = [];
    }
    const handlers = this.messageHandlers[type];
    const index = handlers.indexOf(handler);
    if (index < 0) {
      handlers.push(handler);
    }
  }

  public removeClickEventHandler(handler: (info: FrameInfo, ev: MouseEvent) => any) {
    const index = this.clickHandlers.indexOf(handler);
    if (index > 0) {
      this.clickHandlers =
        this.clickHandlers.slice(0, index).concat(this.clickHandlers.slice(index + 1));
    }
  }

  public unregisterFrame(key: string): void {
    if (!(key in this.frames)) {
      throw new Error('Illegal state.');
    }

    const { panelID } = this.frames[key];
    this.panelIDToFrameIDs[panelID].delete(key);
    if (this.panelIDToFrameIDs[panelID].size === 0) {
      delete this.panelIDToFrameIDs[panelID];
    }

    delete this.frames[key];
    delete this.ephemeralStyles[key];
  }

  private forEachFrameInTab(tabID: string, proc: (frameID: string) => void) {
    if (
      this.pages === null
      || !(tabID in this.panelIDToFrameIDs)
    ) {
      throw new Error('Illegal state.');
    }

    this.panelIDToFrameIDs[tabID].forEach(id => proc(id));

    for (const popupID in this.pages.popups) {
      const popup = this.pages.popups[popupID];
      if (popup.parent === tabID) {
        this.panelIDToFrameIDs[popupID].forEach(id => proc(id));
      }
    }
  }

  private notifyVisibilityChangeToWindow(target: Window, visible: boolean) {
    this.postMessageToWindow(target, ViewerToContent.MessageType.NotifyVisibilityChange, visible);
  }

  private notifyVisibilityChange(tabID: string, visible: boolean) {
    this.forEachFrameInTab(tabID, async (frameID) => {
      const ref = this.getRef(frameID);
      if (ref !== undefined) {
        const wnd = await getContentWindow(ref);
        if (wnd !== null) {
          this.notifyVisibilityChangeToWindow(wnd, visible);
        }
      }
    });
  }

  public async updateState(newState: AppStates) {
    const { uimode, preference, contentStorage, markers, pages } = newState;
    const uimodeIsUpdated = (() => {
      if (this.uimode === undefined || this.uimode !== uimode) {
        return true;
      }
      if (this.uimode.ttsMode !== uimode.ttsMode) {
        return true;
      }
      if (this.uimode.mode !== uimode.mode) {
        return true;
      }

      return false;
    })();
    const preferenceIsUpdated = this.preference !== preference;
    const storageIsUpdated = this.contentStorage !== contentStorage;
    const markersAreUpdated = this.markers !== markers;

    this.uimode = uimode;
    this.preference = preference;
    this.contentStorage = contentStorage;
    this.markers = markers;

    const prevPages = this.pages;

    // Do it before forEachFrameInTab is called.
    this.pages = pages;

    const newVisibleTabID = pages.tabs[pages.activeTab].id;
    const lastVisibleTabID = (
      prevPages !== null && prevPages.activeTab < prevPages.tabs.length
        ? prevPages.tabs[prevPages.activeTab].id
        : null
    );
    if (lastVisibleTabID !== newVisibleTabID) {
      if (lastVisibleTabID !== null && findTabByID(pages, lastVisibleTabID) !== null) {
        this.notifyVisibilityChange(lastVisibleTabID, false);
      }
      // Notification to the new frame is done at the time we receive PageStartup.
      if (prevPages !== null && findTabByID(prevPages, newVisibleTabID) !== null) {
        this.notifyVisibilityChange(newVisibleTabID, true);
      }
    }

    const newActiveFrameId = getActiveFrameId(this.pages);
    const focusIsUpdated = newActiveFrameId !== this.lastActiveFrameId;

    if (
      !preferenceIsUpdated
      && !storageIsUpdated
      && !markersAreUpdated
      && !focusIsUpdated
      && !uimodeIsUpdated
    ) {
      return;
    }

    if (focusIsUpdated) {
      if (this.lastActiveFrameId !== null) {
        const wnd = this.frames[this.lastActiveFrameId].ref.contentWindow;
        if (wnd !== null) {
          // FIXME: frameIDをそのまま使っている
          this.notifyFocusChange(wnd, false, newActiveFrameId);
        }
      }

      const activeFrame = this.frames[newActiveFrameId];
      if (activeFrame.started) {
        const wnd = activeFrame.ref.contentWindow;
        if (wnd !== null) {
          // FIXME: frameIDをそのまま使っている
          this.notifyFocusChange(wnd, true, newActiveFrameId);
          this.lastActiveFrameId = newActiveFrameId;
        }
      }
    }

    for (const key of Object.keys(this.frames)) {
      const frame = this.frames[key];
      const wnd = await getContentWindow(frame.ref);
      if (wnd === null) {
        continue;
      }

      if (uimodeIsUpdated) {
        this.notifyViewerUIModeChange(wnd, uimode);
      }
      if (preferenceIsUpdated) {
        this.applyPreferenceToWindow(wnd, key);
      }
      if (storageIsUpdated) {
        this.notifyContentStorageToWindow(wnd);
      }
      if (markersAreUpdated) {
        this.postMessageToWindow(wnd, ViewerToContent.MessageType.NotifyUpdateMarker, markers);
      }
    }
  }

  // TODO : Should also return 'loaded' state.
  public getRef(key: string): HTMLIFrameElement | undefined {
    return (key in this.frames) ? this.frames[key].ref : undefined;
  }

  public getEphemeralStyle(key: string): EphemeralStyle | undefined {
    return (key in this.ephemeralStyles) ? this.ephemeralStyles[key] : undefined;
  }

  public notifyVisualProperties(
    key: string,
    visibleRect: Rectangle,
    visualScale: number,
    rootContainerSize: Dimension,
    popupCollapsed: boolean,
  ) {
    this.ephemeralStyles[key] = { visibleRect, visualScale, rootContainerSize, popupCollapsed };

    const ref = this.getRef(key);
    if (ref !== undefined && ref.contentWindow !== null) {
      this.notifyVisualPropertiesToWindow(ref.contentWindow, this.ephemeralStyles[key]);
    }
  }

  public notifyPopupRect(key: string, rect: Rectangle) {
    this.popupRects[key] = rect;

    const ref = this.getRef(key);
    if (ref !== undefined && ref.contentWindow !== null) {
      this.notifyPopupRectToWindow(ref.contentWindow, this.popupRects[key]);
    }
  }

  public postMessage<T extends ViewerToContent.MessageType>(key: string,
                                                            type: T,
                                                            data?: ViewerToContent.Data<T>) {
    const ref = this.getRef(key);
    if (ref !== undefined && ref.contentWindow !== null) {
      return this.postMessageToWindow(ref.contentWindow, type, data);
    }
  }

  private createFrameIDText(info: FrameInfo) {
    return `var ARK_FRAME_INFO = ${JSON.stringify(info)};`;
  }

  private injectScriptAndStyleToDoc(doc: Document, info: FrameInfo): void {
    const win = doc.defaultView;
    if (win === null) {
      return;
    }

    this.reverseRefs.set(win, info);
    win.addEventListener('click', this.handleClick.bind(this, info), true);
    // For XHTML content document <head> element.
    const head = doc.getElementsByTagNameNS(xmlns.html, 'head')[0];
    if (head !== undefined) {
      const script1 = doc.createElement('script');
      script1.type = 'text/javascript';
      script1.appendChild(doc.createTextNode(this.createFrameIDText(info)));
      head.appendChild(script1);

      const script2 = doc.createElement('script');
      script2.src = `${this.baseDir}/assets/ark-book.js`;
      script2.type = 'text/javascript';
      head.appendChild(script2);

      const link = doc.createElement('link');
      link.rel = 'stylesheet';
      link.href = `${this.baseDir}/assets/ark-book.css`;
      head.appendChild(link);
    }

    // For SVG embedded by <object> element.
    const object = doc.getElementsByTagNameNS(xmlns.html, 'object')[0] as HTMLObjectElement;
    if (object !== undefined) {
      // 何らかの理由でobjectの中身がリロードされてしまった場合に備える
      // 例: https://bugs.chromium.org/p/chromium/issues/detail?id=952106
      object.addEventListener('load', () => this.injectScriptToSVGObject(object, info));

      this.injectScriptToSVGObject(object, info);
    }

    // TODO : SVG content documentには未対応
  }

  private injectScriptToSVGObject(object: HTMLObjectElement, info: FrameInfo): void {
    let svgDoc: Document | null = null;
    try {
      // Safari causes "NotSupportedError" on loading, while most browsers just return "null".
      svgDoc = object.getSVGDocument();
    } catch (e) { /* ignore */ }

    // 動的生成した固定レイアウトコンテンツの body > object を想定
    if (svgDoc == null) {
      svgDoc = object.contentDocument;
    }

    if (svgDoc == null) {
      console.warn('Failed to inject viewer script: failed to get SVG document element.');
      return;
    }

    const win = svgDoc.defaultView;
    if (win === null) {
      console.error(
        'Failed to inject viewer script: svgDoc.defaultView is null (null browsing context?).',
      );
      return;
    }

    if ('Ark' in win) {
      // Already injected.
      return;
    }

    this.reverseRefs.set(win, info);
    win.addEventListener('click', this.handleClick.bind(this, info), true);

    const svgDocElement = svgDoc.documentElement;
    if (svgDocElement === null) {
      return;
    }

    const script1 = svgDoc.createElementNS(xmlns.svg, 'script');
    script1.appendChild(svgDoc.createTextNode(this.createFrameIDText(info)));
    svgDocElement.appendChild(script1);

    const script2 = svgDoc.createElementNS(xmlns.svg, 'script');
    script2.setAttributeNS(xmlns.xlink, 'href', `${this.baseDir}/assets/ark-book.js`);
    svgDocElement.appendChild(script2);

    const style = svgDoc.createElementNS(xmlns.svg, 'style');
    style.appendChild(svgDoc.createTextNode(`@import url(${this.baseDir}/assets/ark-book.css);`));
    svgDocElement.appendChild(style);
  }

  private notifyVisualPropertiesToWindow(target: Window, data: EphemeralStyle) {
    // XXX : Assumes that EphemeralStyle is compatible with VisualPropertiesMessage.
    this.postMessageToWindow(target, ViewerToContent.MessageType.VisualPropertiesChanged, data);
  }

  private notifyPopupRectToWindow(target: Window, data: Rectangle) {
    this.postMessageToWindow(target, ViewerToContent.MessageType.PopupRectChanged, data);
  }

  private applyPreferenceToWindow(target: Window, frameID: string): void {
    const { preference } = this;
    if (preference !== undefined) {
      if (this.frames[frameID].shouldApplyReflowableContentSettings) {
        this.setFontFamily(target, preference.fontFamily);
        this.setFontSize(target, preference.fontSize);
        this.setLineHeight(target, preference.lineHeight);
        this.setPadding(target, preference.padding);
      }

      this.setGeneralRuby(target, preference.enableGeneralRuby);
      this.setColorScheme(target,
                          preference.colorScheme,
                          preference.enableColoringOfPages,
                          preference.customPageColorIndex.backgroundColorIndex,
                          preference.customPageColorIndex.textColorIndex);
      this.setContentFeatureFlags(target, preference.contentFeatureFlags);
    }
  }

  private notifyContentStorageToWindow(target: Window): void {
    const { contentStorage } = this;
    if (contentStorage !== undefined) {
      this.postMessageToWindow(target,
                               ViewerToContent.MessageType.NotifyContentDataUpdated,
                               contentStorage);
    }
  }

  private notifyFocusChange(target: Window, focus: boolean, activeFrameID: string): void {
    this.postMessageToWindow(
      target,
      ViewerToContent.MessageType.NotifyFocusChange,
      { focus, activeFrameID },
    );
  }

  private notifyViewerUIModeChange(target: Window, uimodeState: UIModeState): void {
    let modeMessage;

    if (uimodeState.ttsMode) {
      modeMessage = 'tts';
    } else {
      switch (uimodeState.mode) {
        case UIMode.Drawing: {
          modeMessage = 'drawing';
          break;
        }
        case UIMode.Marker: {
          modeMessage = 'marker';
          break;
        }
        default: {
          modeMessage = 'default';
          break;
        }
      }
    }

    this.postMessageToWindow(
      target,
      ViewerToContent.MessageType.NotifyViewerUIModeChange,
      modeMessage,
    );
  }

  private setGeneralRuby(target: Window, enabled: boolean): void {
    this.postMessageToWindow(target, ViewerToContent.MessageType.SetGeneralRuby, enabled);
  }

  private getFontFamilyName(family: FontFamily): string {
    switch (family) {
      case FontFamily.Textbook:
        return 'textbook';
      case FontFamily.Serif:
        return 'serif';
      case FontFamily.SansSerif:
        return 'sans-serif';
    }
  }

  private setFontFamily(target: Window, family: FontFamily): void {
    this.postMessageToWindow(target,
                             ViewerToContent.MessageType.SetFontFamily,
                             this.getFontFamilyName(family));
  }

  private setFontSize(target: Window, size: number): void {
    this.postMessageToWindow(target, ViewerToContent.MessageType.SetFontSize, size);
  }

  private setLineHeight(target: Window, size: number): void {
    this.postMessageToWindow(target, ViewerToContent.MessageType.SetLineHeight, size);
  }

  private setPadding(target: Window, size: number): void {
    this.postMessageToWindow(target, ViewerToContent.MessageType.SetPadding, size);
  }

  private setColorScheme(
    target: Window,
    colorScheme: ColorScheme,
    coloringEnabled: boolean,
    backgroundColorIndex: number,
    textColorIndex: number,
  ): void {
    const backgroundColor = sneCustomColors['out'][backgroundColorIndex];
    const textColor = sneCustomColors['out'][textColorIndex];

    const colorSchemeData = {
      coloringEnabled,
      type: colorScheme,
      userSettingBackgroundColor: {
        red: backgroundColor.red,
        green: backgroundColor.green,
        blue: backgroundColor.blue,
        alpha: 1,
      },
      userSettingTextColor: {
        red: textColor.red,
        green: textColor.green,
        blue: textColor.blue,
        alpha: 1,
      },
    };
    this.postMessageToWindow(target, ViewerToContent.MessageType.SetColorScheme, colorSchemeData);
  }

  private setContentFeatureFlags(target: Window, flags: { [featureID: string]: boolean }): void {
    this.postMessageToWindow(target, ViewerToContent.MessageType.SetContentFeatureFlags, flags);
  }

  private postMessageToWindow<T extends ViewerToContent.MessageWithOutData>(
    target: Window,
    type: T,
    data?: ViewerToContent.Data<T>,
  ): ViewerToContent.Response<T>;
  private postMessageToWindow<T extends ViewerToContent.MessageWithData>(
    target: Window,
    type: T,
    data: ViewerToContent.Data<T>,
  ): ViewerToContent.Response<T>;
  private postMessageToWindow(
    target: Window,
    type: ViewerToContent.MessageType,
    data?: ViewerToContent.Data<ViewerToContent.MessageType>,
  ): ViewerToContent.Response<ViewerToContent.MessageType>;
  private postMessageToWindow(
    target: Window,
    type: ViewerToContent.MessageType,
    data: ViewerToContent.Data<ViewerToContent.MessageType>,
  ) {
    return ViewerToContent.postMessageToWindow(target, type, data);
  }

  private handleClick = (info: FrameInfo, ev: MouseEvent) => {
    for (const handler of this.clickHandlers) {
      handler(info, ev);
      if (ev.defaultPrevented) {
        break;
      }
    }
  }

  public sendMarkerMessage(markerInfo: ReadonlyArray<MarkerInfo>) {
    forEachIterator(iterateEntries(this.frames), ([_key, frame]) => {
      this.postMessageToWindow(
        frame.ref.contentWindow!,
        ViewerToContent.MessageType.NotifyUpdateMarker,
        markerInfo,
      );
    });
  }

  public sendTemporaryMarkerMessage<T extends ViewerToContent.TemporaryMarkerMessageType>(
    target: Window | null,
    type: T,
    x: number,
    y: number,
  ) {
    if (target !== null) {
      return this.postMessageToWindow(target, type, [x, y]);
    }
  }

  public sendSelectMarkerMessage<_T extends ViewerToContent.TemporaryMarkerMessageType>(
    target: Window | null,
    x: number,
    y: number,
  ) {
    if (target !== null) {
      return this.postMessageToWindow(target, ViewerToContent.MessageType.SelectMarker, [x, y]);
    }
  }

  // FIXME: ポップアップ未対応(タブ内のiframeにしか対応していない)
  public frameFromPoint(x: number, y: number): HTMLIFrameElement | undefined {
    if (this.pages === null) {
      return;
    }

    const { tabs, activeTab } = this.pages;
    const activeFrameIDs = this.panelIDToFrameIDs[tabs[activeTab].id];

    for (const key of activeFrameIDs) {
      const iframe = this.frames[key].ref;
      const rect = iframe.getBoundingClientRect();
      if (rect.left <= x && x < rect.right && rect.top <= y && y < rect.bottom) {
        return iframe;
      }
    }
  }

  public getInfo(iframe: HTMLIFrameElement) {
    return this.reverseRefs.get(iframe.contentWindow!);
  }

  public broadcastContentMessage(data: any) {
    forEachIterator(iterateEntries(this.frames), ([_key, frame]) => {
      this.postMessageToWindow(
        frame.ref.contentWindow!,
        ViewerToContent.MessageType.ContentMessage,
        data,
      );
    });
  }
}

let globalFrameStore: FrameStore | null = null;

export function getGlobalFrameStore() {
  if (globalFrameStore === null) {
    throw new Error('Illegal state.');
  }

  return globalFrameStore;
}

export function initGlobalFrameStore(store: Store<AppStates>) {
  if (globalFrameStore !== null) {
    throw new Error('Illegal state.');
  }
  globalFrameStore = new FrameStore();

  // subscribe preference and contentStorage state
  store.subscribe(() => {
    globalFrameStore!.updateState(store.getState());
  });
}

export default FrameStore;
